import { Helmet } from "react-helmet-async";

import { organizationSchema, siteMetadata } from "../../../content/siteMetadata";

/**
 * Compose absolute URLs for metadata and structured data.
 * Translation: Compose absolute URLs for metadata and structured data.
 *
 * @param {string} path - Relative site path.
 * @returns {string} Absolute site URL.
 */
const toAbsoluteUrl = (path) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const absoluteUrl = `${siteMetadata.baseUrl}${normalizedPath}`;

  console.log("[VALUE] - Absolute SEO URL:", absoluteUrl);
  return absoluteUrl;
};

/**
 * Shared SEO component for route-level metadata.
 * Translation: Shared SEO component for route-level metadata.
 *
 * @param {Object} props - Metadata props.
 * @returns {JSX.Element} Helmet metadata output.
 */
const Seo = ({
  title,
  description,
  pathname,
  image,
  imageAlt,
  articlePublishedAt,
  articleModifiedAt,
  articleAuthor,
}) => {
  const pageTitle = title || siteMetadata.defaultTitle;
  const pageDescription = description || siteMetadata.defaultDescription;
  const pagePathname = pathname || "/";
  const pageImage = image || siteMetadata.socialImage;
  const pageUrl = toAbsoluteUrl(pagePathname);
  const pageImageUrl = pageImage.startsWith("http")
    ? pageImage
    : toAbsoluteUrl(pageImage);

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${siteMetadata.baseUrl}/#website`,
    url: `${siteMetadata.baseUrl}/`,
    name: siteMetadata.siteName,
    publisher: { "@id": `${siteMetadata.baseUrl}/#organization` },
  };

  const webpageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${pageUrl}#webpage`,
    url: pageUrl,
    name: pageTitle,
    description: pageDescription,
    isPartOf: { "@id": `${siteMetadata.baseUrl}/#website` },
  };

  const articleSchema = articlePublishedAt
    ? {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        headline: pageTitle,
        datePublished: articlePublishedAt,
        dateModified: articleModifiedAt || articlePublishedAt,
        description: pageDescription,
        mainEntityOfPage: {
          "@id": `${pageUrl}#webpage`,
        },
        author: {
          "@type": articleAuthor ? "Person" : "Organization",
          name: articleAuthor || siteMetadata.siteName,
        },
        publisher: { "@id": `${siteMetadata.baseUrl}/#organization` },
        image: pageImageUrl,
      }
    : null;

  console.log("[LOG] - Rendering SEO metadata:", {
    pageTitle,
    pagePathname,
    hasArticleSchema: Boolean(articleSchema),
  });

  return (
    <Helmet>
      <html lang="en" />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={siteMetadata.defaultKeywords.join(", ")} />
      <meta name="publisher" content={siteMetadata.siteName} />
      <meta name="theme-color" content="#050505" />
      <meta name="author" content={siteMetadata.siteName} />
      <meta name="robots" content="index, follow, max-image-preview:large" />
      <link rel="canonical" href={pageUrl} />

      <meta property="og:type" content={articleSchema ? "article" : "website"} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImageUrl} />
      <meta property="og:image:alt" content={imageAlt || siteMetadata.socialImageAlt} />
      <meta property="og:site_name" content={siteMetadata.siteName} />
      <meta property="og:locale" content="en_US" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImageUrl} />
      <meta
        name="twitter:image:alt"
        content={imageAlt || siteMetadata.socialImageAlt}
      />

      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
      {articleSchema ? (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      ) : null}
    </Helmet>
  );
};

export default Seo;
